/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  CrownIcon,
  LightningIcon,
  CheckCircleIcon,
  TwitterLogoIcon,
  WhatsappLogoIcon,
  TelegramLogoIcon,
  LinkedinLogoIcon,
  FacebookLogoIcon,
  RedditLogoIcon,
  CopyIcon,
  TicketIcon,
  EnvelopeIcon,
  TrophyIcon,
} from "@phosphor-icons/react";
import { api, getOrCreateSession } from "@/lib/api";
import Logo from "@/components/Logo";
import LanguageSwitcher from "@/components/LanguageSwitcher";

const PLATFORMS = [
  { id: "twitch", name: "Twitch", group: "twitch" },
  { id: "chaturbate", name: "Chaturbate", group: "cam" },
  { id: "onlyfans", name: "OnlyFans", group: "cam" },
  { id: "pornhub", name: "Pornhub", group: "cam" },
  { id: "camsoda", name: "Camsoda", group: "cam" },
  { id: "stripchat", name: "Stripchat", group: "cam" },
  { id: "manyvids", name: "ManyVids", group: "cam" },
  { id: "other", name: "Other", group: "other" },
];

const GROUP_META = {
  twitch: { label: "Twitch", color: "#9146FF" },
  cam: { label: "Cam platforms", color: "#FF007F" },
  other: { label: "Other", color: "#00E5FF" },
};

const VipClaim = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const sessionId = getOrCreateSession();
  const [status, setStatus] = useState(null);
  const [streamerName, setStreamerName] = useState("");
  const [platform, setPlatform] = useState("twitch");
  const [email, setEmail] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [claimed, setClaimed] = useState(null);
  const [ref] = useState(search.get("ref") || "");

  const refresh = async () => {
    try {
      const r = await api.get("/promo/vip");
      setStatus(r.data);
    } catch (e) { /* noop */ }
  };

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 15000);
    return () => clearInterval(t);
  }, []);

  const activeGroup = PLATFORMS.find((p) => p.id === platform)?.group || "other";
  const groupRemaining = status?.remaining_by_group?.[activeGroup] ?? 0;
  const groupTotal = status?.quotas?.[activeGroup] ?? 0;

  const claim = async (e) => {
    e?.preventDefault();
    if (!streamerName.trim()) {
      toast.error("Enter your streamer name");
      return;
    }
    setClaiming(true);
    try {
      const r = await api.post("/promo/vip/claim", {
        session_id: sessionId,
        streamer_name: streamerName.trim(),
        platform,
        email: email.trim(),
        ref: ref || undefined,
      });
      setClaimed(r.data);
      refresh();
      toast.success("You're in. 7 days of Pro unlocked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Claim failed");
    } finally {
      setClaiming(false);
    }
  };

  const totalRemaining = status?.slots_remaining ?? 300;
  const totalSlots = status?.slots_total ?? 300;
  const pct = totalSlots > 0 ? ((totalSlots - totalRemaining) / totalSlots) * 100 : 0;

  // Share links point to /share/vip/{ref} so each social platform picks up the personalised OG image
  const buildShareUrl = (code) =>
    `${window.location.origin}${code ? `/share/vip/${code}` : "/vip"}`;
  const text = encodeURIComponent(
    "LUCRÜ is giving the top 300 streamers 1 week of AI voice co-host — free. +3 days every friend you bring:"
  );
  const share = (net, code) => {
    const u = encodeURIComponent(buildShareUrl(code));
    const map = {
      twitter: `https://twitter.com/intent/tweet?text=${text}&url=${u}`,
      whatsapp: `https://wa.me/?text=${text}%20${u}`,
      telegram: `https://t.me/share/url?url=${u}&text=${text}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${u}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${u}`,
      reddit: `https://www.reddit.com/submit?url=${u}&title=${text}`,
    };
    window.open(map[net], "_blank", "noopener,noreferrer,width=640,height=640");
  };

  const copyLink = (code) => {
    navigator.clipboard.writeText(buildShareUrl(code));
    toast.success("VIP link copied");
  };

  return (
    <div className="min-h-screen bg-[#05050A] text-white">
      <nav className="relative z-10 flex items-center justify-between px-6 md:px-8 py-5 max-w-7xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <Logo size={38} withBackground />
          <div className="flex flex-col leading-none">
            <span className="font-display font-black text-lg">LUCRÜ</span>
          </div>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link to="/leaderboard" data-testid="vip-leaderboard-link">
            <button className="btn-pill btn-ghost text-xs"><TrophyIcon size={12} /> Leaderboard</button>
          </Link>
        </div>
      </nav>

      <section className="max-w-4xl mx-auto px-6 md:px-8 pt-6 pb-20">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#FF007F]/40 bg-[#FF007F]/5 text-xs text-[#FF007F] mb-6">
            <CrownIcon weight="fill" size={12} /> Launch program · 300 slots · 1 week Pro free
          </div>
          <h1 className="font-display text-4xl md:text-6xl font-black tracking-tight leading-[0.95]">
            Top 300 streamers.
            <br />
            <span className="text-gradient">1 week of Pro.</span>
            <br />
            On the house.
          </h1>
          <p className="text-white/60 mt-5 max-w-xl mx-auto text-sm md:text-base leading-relaxed">
            Every supported platform gets a reserved quota so no vertical dominates. Bring a friend after you claim and get +3 free days per referral.
          </p>
          {ref && (
            <div className="inline-flex items-center gap-2 text-xs mt-5 px-3 py-1.5 rounded-full border border-[#39FF14]/30 bg-[#39FF14]/5 text-[#39FF14]">
              <CheckCircleIcon size={12} weight="fill" />
              Invited by <span className="font-mono">{ref}</span> — they'll earn +3 bonus days when you claim
            </div>
          )}
        </motion.div>

        {/* Per-group counters */}
        {status && (
          <motion.div
            className="grid grid-cols-3 gap-3 mt-10"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            data-testid="vip-group-counters"
          >
            {Object.entries(status.quotas || {}).map(([g, total]) => {
              const claimedCount = (status.claimed_by_group || {})[g] || 0;
              const remaining = Math.max(0, total - claimedCount);
              const groupPct = total > 0 ? (claimedCount / total) * 100 : 0;
              const meta = GROUP_META[g] || { label: g, color: "#A0A0B0" };
              const isActive = activeGroup === g;
              return (
                <div
                  key={g}
                  className={`glass rounded-xl p-4 ${isActive ? "border-2" : "border border-[#222233]"}`}
                  style={{ borderColor: isActive ? meta.color : undefined }}
                  data-testid={`group-counter-${g}`}
                >
                  <div className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: meta.color }}>
                    {meta.label}
                  </div>
                  <div className="font-display text-2xl md:text-3xl font-black mt-1">
                    <span style={{ color: remaining === 0 ? "rgba(255,255,255,0.3)" : meta.color }}>{remaining}</span>
                    <span className="text-white/30 text-sm md:text-base"> / {total}</span>
                  </div>
                  <div className="h-1 rounded-full bg-[#0F0F16] mt-2 overflow-hidden">
                    <div className="h-full transition-all duration-500" style={{ width: `${groupPct}%`, background: meta.color }} />
                  </div>
                </div>
              );
            })}
          </motion.div>
        )}

        {/* Global progress */}
        <div className="mt-4 flex items-center justify-between text-[11px] text-white/40">
          <span>{totalSlots - totalRemaining} claimed · {totalRemaining} total slots left</span>
          <span>{Math.round(pct)}% full</span>
        </div>

        {claimed ? (
          <motion.div
            className="glass rounded-2xl p-8 mt-6"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            data-testid="vip-claimed"
          >
            <div className="text-center mb-5">
              <CheckCircleIcon size={56} weight="fill" className="mx-auto text-[#39FF14] mb-3" />
              <div className="font-display text-2xl font-black mb-1">You're in · position #{claimed.position}</div>
              <div className="text-sm text-white/60">
                Pro unlocked until <span className="text-[#39FF14] font-semibold">{new Date(claimed.vip_until).toLocaleDateString()}</span>
              </div>
            </div>

            {/* Referral promo */}
            <div className="bg-[#05050A] border border-[#FF007F]/30 rounded-xl p-4 mb-5">
              <div className="text-[11px] uppercase tracking-widest text-[#FF007F] mb-2">Your referral link · +3 days per friend</div>
              <div className="flex items-center gap-1.5">
                <input
                  readOnly
                  value={buildShareUrl(claimed.referral_code)}
                  className="flex-1 bg-[#0F0F16] border border-[#222233] rounded-lg px-3 py-2 text-xs font-mono text-white/80"
                  data-testid="claimed-ref-link"
                />
                <button
                  onClick={() => copyLink(claimed.referral_code)}
                  className="w-10 h-10 rounded-lg bg-gradient-to-br from-[#00E5FF] to-[#39FF14] text-[#05050A] flex items-center justify-center font-bold"
                  data-testid="claimed-ref-copy"
                >
                  <CopyIcon size={14} weight="fill" />
                </button>
              </div>
              <div className="flex items-center gap-1.5 mt-2">
                {[
                  { id: "twitter", icon: <TwitterLogoIcon size={14} /> },
                  { id: "whatsapp", icon: <WhatsappLogoIcon size={14} /> },
                  { id: "telegram", icon: <TelegramLogoIcon size={14} /> },
                  { id: "linkedin", icon: <LinkedinLogoIcon size={14} /> },
                  { id: "facebook", icon: <FacebookLogoIcon size={14} /> },
                  { id: "reddit", icon: <RedditLogoIcon size={14} /> },
                ].map((s) => (
                  <button
                    key={s.id}
                    onClick={() => share(s.id, claimed.referral_code)}
                    className="flex-1 h-9 rounded-lg border border-[#222233] flex items-center justify-center text-white/60 hover:text-white hover:border-[#00E5FF]"
                    data-testid={`claimed-share-${s.id}`}
                  >
                    {s.icon}
                  </button>
                ))}
              </div>
            </div>

            <button onClick={() => navigate("/dashboard")} className="btn-pill btn-primary w-full justify-center" data-testid="vip-go-dashboard">
              <LightningIcon size={14} weight="fill" /> Open dashboard
            </button>
          </motion.div>
        ) : (
          <motion.form
            onSubmit={claim}
            className="glass rounded-2xl p-6 md:p-8 mt-6 space-y-4"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider mb-1 block">Your streamer handle</label>
              <input
                value={streamerName}
                onChange={(e) => setStreamerName(e.target.value)}
                placeholder="e.g. xqc, pokimane, your_cam_name"
                data-testid="vip-streamer-name"
                className="w-full bg-[#05050A] border border-[#222233] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF007F]"
              />
            </div>
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider mb-1 block flex items-center gap-1.5">
                <EnvelopeIcon size={11} /> Email <span className="text-white/30 normal-case">· for your day-6 reminder</span>
              </label>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                placeholder="you@domain.com (optional)"
                data-testid="vip-email"
                className="w-full bg-[#05050A] border border-[#222233] rounded-xl px-4 py-3 text-sm outline-none focus:border-[#FF007F]"
              />
            </div>
            <div>
              <label className="text-[11px] text-white/50 uppercase tracking-wider mb-1 block">Main platform</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {PLATFORMS.map((p) => {
                  const remaining = status?.remaining_by_group?.[p.group] ?? 0;
                  const disabled = status && remaining <= 0;
                  return (
                    <button
                      type="button"
                      key={p.id}
                      onClick={() => !disabled && setPlatform(p.id)}
                      disabled={disabled}
                      data-testid={`vip-platform-${p.id}`}
                      className={`text-xs py-2 rounded-lg border transition-colors ${
                        platform === p.id
                          ? "border-[#FF007F] text-[#FF007F] bg-[#FF007F]/5"
                          : "border-[#222233] text-white/60 hover:border-white/40"
                      } ${disabled ? "opacity-30 cursor-not-allowed" : ""}`}
                    >
                      {p.name}
                    </button>
                  );
                })}
              </div>
            </div>

            {status && (
              <div className="text-[11px] text-white/50 text-center">
                {groupRemaining} slot{groupRemaining !== 1 ? "s" : ""} left in {GROUP_META[activeGroup]?.label} ({groupTotal} total)
              </div>
            )}

            <button
              type="submit"
              disabled={claiming || groupRemaining <= 0}
              data-testid="vip-claim-button"
              className="btn-pill btn-primary w-full justify-center disabled:opacity-40"
            >
              <TicketIcon size={16} weight="fill" />
              {groupRemaining <= 0
                ? `${GROUP_META[activeGroup]?.label} quota is full`
                : claiming
                  ? "Claiming…"
                  : "Claim my free week of Pro"}
            </button>
            <p className="text-[11px] text-white/40 text-center">
              No payment info required. 7 days later your account rolls back to the 30-day free trial — never charged automatically.
            </p>
          </motion.form>
        )}

        {/* Pre-claim viral share bar */}
        {!claimed && (
          <motion.div
            className="glass rounded-2xl p-5 mt-5 flex items-center justify-between flex-wrap gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
          >
            <div className="text-xs text-white/60">
              Know a streamer? Send them a slot — one click:
            </div>
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => copyLink()}
                className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-[#222233] text-[11px] hover:border-[#00E5FF] hover:text-[#00E5FF]"
                data-testid="vip-copy-link"
              >
                <CopyIcon size={12} /> Copy link
              </button>
              {[
                { id: "twitter", icon: <TwitterLogoIcon size={14} /> },
                { id: "whatsapp", icon: <WhatsappLogoIcon size={14} /> },
                { id: "telegram", icon: <TelegramLogoIcon size={14} /> },
                { id: "facebook", icon: <FacebookLogoIcon size={14} /> },
                { id: "linkedin", icon: <LinkedinLogoIcon size={14} /> },
                { id: "reddit", icon: <RedditLogoIcon size={14} /> },
              ].map((s) => (
                <button
                  key={s.id}
                  onClick={() => share(s.id)}
                  className="w-9 h-9 rounded-full border border-[#222233] flex items-center justify-center text-white/60 hover:text-white hover:border-[#00E5FF]"
                  data-testid={`vip-share-${s.id}`}
                  title={`Share on ${s.id}`}
                >
                  {s.icon}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </section>
    </div>
  );
};

export default VipClaim;
